<template>
  <div id="list-recipients">
    <div v-if="list_id && recipients">
      <b-button variant="link" class="p-0" @click="recipient_index -= 1" :disabled="!recipient_index">
        <i class="fa fa-fw fa-chevron-left"></i>
      </b-button>
      <strong class="mx-2">
        {{ recipient_index+1 }}/{{ recipients.length }} 
        {{ trans('recipients') }}
      </strong>
      <b-button variant="link" class="p-0" @click="recipient_index += 1" :disabled="recipients.length == 0 || recipient_index >= recipients.length-1">
        <i class="fa fa-fw fa-chevron-right"></i>
      </b-button>
    </div>
    <p v-if="busy" class="mx-3 my-2">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="mx-3 my-2 text-danger">
      {{ error }}
    </p>
    <div class="recipient m-2" v-if="!busy && !isEmpty(recipient)">
      <div>
        <div v-if="recipient.name">{{ recipient.name }}</div>
        <div v-if="recipient.mobile && canAccessClientContactInformation">{{ formatPhone(recipient.mobile) }}</div>
        <div v-if="recipient.email && canAccessClientContactInformation">{{ recipient.email }}</div>
      </div>
      <div v-if="recipient.extra">
        <div v-if="recipient && recipient.extra && recipient.extra.activity" class="mt-2">
          <small class="text-muted">{{ trans('activity') }}</small>
          <div>{{ activityToString(recipient.extra.activity) }}</div>
        </div>
        <div v-if="!!getOccurrence(recipient)" class="mt-2">
          <small class="text-muted">{{ trans('occurrence') }}</small>
          <div>{{ occurrenceToString(getOccurrence(recipient)) }}</div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.membership" class="mt-2">
          <small class="text-muted">{{ trans('membership') }}</small>
          <div>{{ recipient.extra.membership.name }}<span v-if="recipient.extra.membership.expired_at">, exp. {{ moment(recipient.extra.membership.expired_at).format('ll') }}</span></div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.custom" class="mt-2">
          <small class="text-muted">{{ trans('custom') }}</small>
          <div>{{ customToString(recipient.extra.custom) }}</div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.multipass" class="mt-2">
          <small class="text-muted">{{ trans('multipass') }}</small>
          <div>
            {{ recipient.extra.multipass.name }}
            <span v-if="recipient.extra.multipass.expired_at">
              , exp. {{ moment(recipient.extra.multipass.expired_at).format('ll') }}
            </span>
            <span>
              , {{ recipient.extra.multipass.has_unlimited_tokens ? '99+' : recipient.extra.multipass.tokens_left }}
              {{ trans('tokens_left').toLowerCase() }}
            </span>
          </div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.bookings" class="mt-2">
          <small class="text-muted">{{ trans('bookings') }}</small>
          <div v-for="booking in recipient.extra.bookings" :key="booking.id">
            {{ booking.name }}
          </div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.participant" class="mt-2">
          <small class="text-muted">{{ trans(recipient.extra.membership ? 'member' : 'participant') }}</small>
          <div v-if="recipient.extra.participant.name">{{ recipient.extra.participant.name }}</div>
          <div v-if="recipient.extra.participant.email && canAccessClientContactInformation">{{ recipient.extra.participant.email }}</div>
          <div v-if="recipient.extra.participant.group_name || placeholders && placeholders.group">
            <span class="text-muted">{{ trans('group') }}:</span>
            {{ recipient.extra.participant.group_name || placeholders.group }}
          </div>
          <div v-if="recipient.extra.participant.allergies">
            <span class="text-muted">{{ trans('allergies') }}:</span>
            {{ recipient.extra.participant.allergies }}
          </div>
          <div v-if="recipient.extra.participant.medical_conditions">
            <span class="text-muted">{{ trans('medical_conditions') }}:</span>
            {{ recipient.extra.participant.medical_conditions }}
          </div>
          <div v-if="recipient.extra.participant.daycare">
            <span class="text-muted">{{ trans('daycare') }}:</span>
            {{ recipient.extra.participant.daycare }}
          </div>
          <div v-if="recipient.extra.participant.pickup_authorized">
            <span class="text-muted">{{ trans('pickup_authorized') }}:</span>
            {{ recipient.extra.participant.pickup_authorized }}
          </div>
          <div v-if="recipient.extra.participant.allow_sunscreen">
            <span class="text-muted">{{ trans('allow_sunscreen') }}:</span>
            {{ recipient.extra.participant.allow_sunscreen }}
          </div>
          <div v-if="recipient.extra.participant.allow_photo">
            <span class="text-muted">{{ trans('allow_photo') }}:</span>
            {{ recipient.extra.participant.allow_photo }}
          </div>
          <div v-if="recipient.extra.participant.allow_leave">
            <span class="text-muted">{{ trans('allow_leave') }}:</span>
            {{ recipient.extra.participant.allow_leave }}
          </div>
          <div v-if="recipient.extra.participant.pickup_password">
            <span class="text-muted">{{ trans('pickup_password') }}:</span>
            {{ recipient.extra.participant.pickup_password }}
          </div>
          <div v-if="recipient.extra.participant.friend">
            <span class="text-muted">{{ trans('friend_pairing') }}:</span>
            {{ recipient.extra.participant.friend }}
          </div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.account_owner" class="mt-2">
          <small class="text-muted">{{ trans('account_owner') }}</small>
          <div v-if="recipient.extra.account_owner.company">{{ recipient.extra.account_owner.company }}</div>
          <div v-if="recipient.extra.account_owner.name">{{ recipient.extra.account_owner.name }}</div>
          <div v-if="recipient.extra.account_owner.email && canAccessClientContactInformation">{{ recipient.extra.account_owner.email }}</div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.other_guardian" class="mt-2">
          <small class="text-muted">{{ trans('other_guardian') }}</small>
          <div v-if="recipient.extra.other_guardian.name">{{ recipient.extra.other_guardian.name }}</div>
          <div v-if="recipient.extra.other_guardian.email && canAccessClientContactInformation">{{ recipient.extra.other_guardian.email }}</div>
          <div v-if="recipient.extra.other_guardian.mobile && canAccessClientContactInformation">{{ formatPhone(recipient.extra.other_guardian.mobile) }}</div>
        </div>
        <div v-if="recipient && recipient.extra && !recipient.extra.other_guardian && recipient.extra.other_parent" class="mt-2">
          <small class="text-muted">{{ trans('other_guardian') }}</small>
          <div v-if="recipient.extra.other_parent.name">{{ recipient.extra.other_parent.name }}</div>
          <div v-if="recipient.extra.other_parent.email && canAccessClientContactInformation">{{ recipient.extra.other_parent.email }}</div>
          <div v-if="recipient.extra.other_parent.mobile && canAccessClientContactInformation">{{ formatPhone(recipient.extra.other_parent.mobile) }}</div>
        </div>
        <div v-if="recipient && recipient.extra && recipient.extra.children && recipient.extra.children.length > 0" class="mt-2">
          <small class="text-muted">{{ trans('children') }}</small>
          <div v-for="child in recipient.extra.children" v-bind:key="child.name">
            {{ childToString(child) }}
          </div>
        </div>
      </div>
    </div>
    <div class="recipient m-2" v-if="isEmpty(recipient)">
      {{ trans('showing_empty_value') }}
    </div>
  </div>
</template>

<style>
  #list-recipients .recipient {
    border-left: 5px solid #929FBA;
    padding-left: 0.5em;
  }
</style>

<script>
  export default {
    props: {
      organization: Object,
      list_id: Number,
    },
    data() {
      return {
        trans: trans,
        formatPhone: _.formatPhone,
        moment: moment,
        isEmpty: _.isEmpty,
        activityToString: _.activityToString,
        occurrenceToString: _.occurrenceToString,
        childToString: _.childToString,
        customToString: _.customToString,

        busy: false,
        error: null,
        recipients: null,
        recipient_index: null,
        placeholders: null,
        loaded_placholders_for_recipient_index: null,
        zoom_meeting: null,
        cache: {},
      }
    },
    computed: {
      canAccessClientContactInformation() {
        return this.organization.auth_user.can_access_client_ci
      },
      recipient() {
        if (this.recipient_index === null || !this.recipients) return null
        return this.recipients[this.recipient_index] || null
      },
    },
    watch: {
      recipient_index() {
        if (!this.recipient) return
        this.$emit('recipient', this.recipient)
        this.hydratePlaceholders()
      },
      list_id() {
        this.onListChange()
      },
    },
    methods: {
      getOccurrence(recipient) {
        if (recipient.extra.occurrence) return recipient.extra.occurrence
        if (recipient.extra.occurrences && recipient.extra.occurrences.length > 0)
          return recipient.extra.occurrences[0]
        return recipient.extra.activity
      },
      hydratePlaceholders() {
        if (!this.organization ||
          !this.recipient ||
          this.loaded_placholders_for_recipient_index === this.recipient_index ||
          this.busy) return
        let url = `/organization/${this.organization.id}/recipient/${this.recipient.id}/placeholders`
        this.busy = true
        this.error = null
        axios.get(url)
        .then(response => {
          this.busy = false
          this.placeholders = response.data
          this.loaded_placholders_for_recipient_index = this.recipient_index
          this.$emit('placeholders', this.placeholders)
          this.$emit('recipient', this.recipient)
          this.hydrateZoomInRecipientExtra()
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.placeholders = null
          this.loaded_placholders_for_recipient_index = null
          this.$emit('placeholders', this.placeholders)
          this.$emit('recipient', this.recipient)
        })
      },
      hydrateZoomInRecipientExtra() {
        if (!this.organization.has_amilia ||
          !this.recipient ||
          !this.recipient.extra ||
          !this.recipient.extra.activity ||
          this.busy) return

        let key = `amilia_activity/${this.recipient.extra.activity.id}`
        if (this.recipient.extra.occurrence)
          key = `amilia_occurrence/${this.recipient.extra.occurrence.id}`

        this.zoom_meeting = this.cache[key]
        if (this.zoom_meeting) {
          this.$set(this.recipient.extra, 'zoom_meeting', this.zoom_meeting)
          this.$emit('recipient', this.recipient)
          return
        }
        if (this.zoom_meeting === null) return

        this.zoom_meeting = null
        this.cache[key] = null
        let url = `/organization/${this.organization.id}/activity/zoom_meeting/${key}`
        if (this.recipient.extra.occurrence)
          url = `/organization/${this.organization.id}/activity/zoom_meeting/${key}`
        axios.get(url)
        .then(response => {
          this.zoom_meeting = {
            id: response.data.id,
            amilia_activity_id: response.data.amilia_activity_id,
            amilia_occurrence_id: response.data.amilia_occurrence_id,
            zoom_id: _.formatZoomMeetingId(response.data.pmi || response.data.zoom_id),
            join_url: response.data.join_url,
            password: response.data.password,
          }
          this.cache[key] = this.zoom_meeting
          this.$set(this.recipient.extra, 'zoom_meeting', this.zoom_meeting)
          this.$emit('recipient', this.recipient)
        })
        .catch(error => {
          this.zoom_meeting = null
          this.cache[key] = null
        })
      },
      onListChange() {
        if (this.list_id == null) return
        let url = `/organization/${this.organization.id}/list/${this.list_id}/recipient?all=true`
        this.recipients = null
        this.recipient_index = null
        this.placeholders = null
        this.$emit('recipient', null)
        this.$emit('placeholders', null)
        this.busy = true
        this.error = null
        axios.get(url)
        .then(response => {
          this.busy = false
          this.recipients = response.data
          this.loaded_placholders_for_recipient_index = null
          this.recipient_index = 0
          this.hydratePlaceholders()
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.listRecipients = this
      if (this.list_id) this.onListChange()
    },
  }
</script>